/**
 * Quick Stats Card
 *
 * Scannable summary of key metrics with trend indicators.
 * Shows DMARC pass rate, messages, sources breakdown, and domain counts.
 */

import { Link } from "react-router-dom";
import {
  BarChart3,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Minus,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Globe,
  ShieldCheck,
} from "lucide-react";

interface QuickStatsCardProps {
  /** DMARC pass rate as a percentage (0-100) */
  dmarcPassRate: number;
  /** Trend change from previous period as percentage points */
  passRateTrend?: number;
  /** Total messages processed */
  messagesProcessed: number;
  /** Trend change from previous period as percentage */
  messagesTrend?: number;
  /** Sources breakdown */
  sources: {
    approved: number;
    pending: number;
    unauthorized: number;
  };
  /** Domain counts */
  domains: {
    total: number;
    atEnforcement: number;
  };
  /** Loading state */
  loading?: boolean;
}

function TrendIndicator({ value, suffix = "" }: { value?: number; suffix?: string }) {
  if (value === undefined || value === 0) {
    return (
      <span className="text-xs text-slate-400 flex items-center gap-0.5">
        <Minus className="w-3 h-3" />
        <span>0{suffix}</span>
      </span>
    );
  }

  const isPositive = value > 0;
  const Icon = isPositive ? TrendingUp : TrendingDown;
  const color = isPositive ? "text-emerald-700" : "text-red-600";

  return (
    <span className={`text-xs ${color} flex items-center gap-0.5`}>
      <Icon className="w-3 h-3" />
      <span>
        {isPositive ? "+" : ""}
        {value.toFixed(1)}
        {suffix}
      </span>
    </span>
  );
}

function formatNumber(num: number): string {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toString();
}

function ProgressBar({ value, color = "amber" }: { value: number; color?: string }) {
  const colorClasses = {
    amber: "bg-amber-500",
    green: "bg-emerald-500",
    red: "bg-red-600",
    slate: "bg-slate-400",
  };

  return (
    <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-500 ${colorClasses[color as keyof typeof colorClasses] || colorClasses.amber}`}
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  );
}

export function QuickStatsCard({
  dmarcPassRate,
  passRateTrend,
  messagesProcessed,
  messagesTrend,
  sources,
  domains,
  loading = false,
}: QuickStatsCardProps) {
  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-4 animate-pulse">
        <div className="h-5 bg-slate-200 rounded w-40 mb-4" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="h-20 bg-slate-100 rounded" />
          <div className="h-20 bg-slate-100 rounded" />
          <div className="h-20 bg-slate-100 rounded" />
          <div className="h-20 bg-slate-100 rounded" />
        </div>
      </div>
    );
  }

  const totalSources = sources.approved + sources.pending + sources.unauthorized;
  const passRateColor =
    dmarcPassRate >= 95 ? "green" : dmarcPassRate >= 80 ? "amber" : "red";

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 pb-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-slate-600" />
          <h3 className="font-semibold text-slate-800">Quick Stats</h3>
          <span className="text-xs text-slate-400">Last 7 days</span>
        </div>
        <Link
          to="/reports"
          className="text-sm text-amber-600 hover:text-amber-700 flex items-center gap-1 transition-colors"
        >
          View Reports
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="px-4 pb-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* DMARC Pass Rate */}
        <Link to="/reports" className="bg-slate-50 rounded-lg p-3 hover:bg-amber-50 transition-colors cursor-pointer block">
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
            DMARC Pass
          </div>
          <div className="flex items-baseline gap-2">
            <span
              className={`text-2xl font-bold ${
                dmarcPassRate >= 95
                  ? "text-emerald-700"
                  : dmarcPassRate >= 80
                    ? "text-amber-600"
                    : "text-red-600"
              }`}
            >
              {dmarcPassRate}%
            </span>
            <TrendIndicator value={passRateTrend} suffix="%" />
          </div>
          <div className="mt-2">
            <ProgressBar value={dmarcPassRate} color={passRateColor} />
          </div>
        </Link>

        {/* Messages Processed */}
        <Link to="/reports" className="bg-slate-50 rounded-lg p-3 hover:bg-amber-50 transition-colors cursor-pointer block">
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
            Messages
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-slate-800">
              {formatNumber(messagesProcessed)}
            </span>
            <TrendIndicator value={messagesTrend} suffix="%" />
          </div>
          <div className="mt-2 text-xs text-slate-500">
            emails processed
          </div>
        </Link>

        {/* Senders (provider-level counts) */}
        <Link to="/sources" className="bg-slate-50 rounded-lg p-3 hover:bg-amber-50 transition-colors cursor-pointer block">
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
            Senders
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-slate-800">{totalSources}</span>
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
            <span className="flex items-center gap-1 text-emerald-700">
              <CheckCircle2 className="w-3 h-3" />
              {sources.approved}
            </span>
            {sources.pending > 0 && (
              <span className="flex items-center gap-1 text-amber-600">
                <Clock className="w-3 h-3" />
                {sources.pending}
              </span>
            )}
            {sources.unauthorized > 0 && (
              <span className="flex items-center gap-1 text-red-600">
                <AlertTriangle className="w-3 h-3" />
                {sources.unauthorized}
              </span>
            )}
          </div>
        </Link>

        {/* Domains */}
        <Link to="/domains" className="bg-slate-50 rounded-lg p-3 hover:bg-amber-50 transition-colors cursor-pointer block">
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
            Domains
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-slate-800">{domains.total}</span>
          </div>
          <div className="mt-2 flex items-center gap-1 text-xs">
            {domains.atEnforcement > 0 ? (
              <span className="flex items-center gap-1 text-emerald-700">
                <ShieldCheck className="w-3 h-3" />
                {domains.atEnforcement} at enforcement
              </span>
            ) : domains.total > 0 ? (
              <span className="flex items-center gap-1 text-slate-500">
                <Globe className="w-3 h-3" />
                monitoring
              </span>
            ) : (
              <span className="text-slate-400">No domains yet</span>
            )}
          </div>
        </Link>
      </div>
    </div>
  );
}